"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Clock, PhoneCall, ShieldCheck, Wrench, CircleAlert as AlertCircle, Navigation, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const neighborhoods = [{
  name: "Historic Downtown Jonesboro",
  desc: "Rapid dispatch along Main St, Courthouse square, and surrounding historic homes.",
  zip: "30236"
}, {
  name: "Tara Boulevard Corridor",
  desc: "Commercial plazas, dining centers, and residential sub-divisions along US-19 / US-41.",
  zip: "30236 / 30238"
}, {
  name: "Lake Spivey & Surrounding",
  desc: "Complete residential emergency coverage for waterfront properties and private estates.",
  zip: "30236"
}, {
  name: "Flint River & Irondale",
  desc: "Fast service for main line backups, sump pump failures, and flood prevention.",
  zip: "30238"
}, {
  name: "North Jonesboro / Morrow Border",
  desc: "Immediate assistance across Clayton State area and northern arterial roads.",
  zip: "30236"
}, {
  name: "South Jonesboro / Lovejoy Border",
  desc: "24/7 coverage for subdivisions, shopping hubs, and southern Clayton County routes.",
  zip: "30238"
}];
const highlights = [{
  title: "Average 30-45 Min Arrival",
  desc: "Our fully stocked mobile service vans are stationed directly across Jonesboro for quick dispatch.",
  icon: Clock
}, {
  title: "Clayton County Licensed",
  desc: "Fully licensed, bonded, and insured master plumbers compliant with all local Georgia plumbing codes.",
  icon: ShieldCheck
}, {
  title: "24/7 Emergency Dispatch",
  desc: "Live local operators taking urgent calls day, night, weekends, and holidays with zero overtime surprises.",
  icon: PhoneCall
}];
export default function JonesboroArea() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="serviceareajonesboro" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          <Badge variant="secondary" className="mb-4 gap-1.5 px-3 py-1 font-medium">
            <MapPin className="size-3.5 text-primary" />
            Clayton County Coverage
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            24/7 Emergency Plumbing Across Jonesboro, GA
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            When a pipe bursts, a sewer backs up, or your water heater gives out, our local technicians are ready to roll. We provide dependable, fast-response plumbing throughout Jonesboro and surrounding Clayton County communities.
          </p>
        </div>

        {/* Feature Highlights Banner */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {highlights.map((item, index) => {
          const IconComponent = item.icon;
          return <div key={item.title} data-index={index} className="bg-card text-card-foreground p-6 rounded-xl border border-border flex items-start gap-4 shadow-sm">
                <div className="p-3 rounded-lg bg-primary/10 text-primary shrink-0">
                  <IconComponent className="size-6" />
                </div>
                <div>
                  <h3 className="font-default font-semibold text-lg text-foreground mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground font-sans leading-normal">
                    {item.desc}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {/* Two-Column Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Local Neighborhoods Grid */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div>
              <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm mb-2">
                <Navigation className="size-4" />
                Local Service Sectors
              </div>
              <h3 className="text-2xl font-default font-bold text-foreground">
                Jonesboro Neighborhoods We Serve
              </h3>
              <p className="text-sm text-muted-foreground font-sans mt-1">
                Our technicians are stationed locally to minimize transit times and stop water damage before it spreads.
              </p>
            </div>

            <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-4" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: "-50px"
          }}>
              {neighborhoods.map((area, index) => <motion.div key={area.name} data-index={index} variants={itemVariants}>
                  <Card className="h-full border-border/80 shadow-xs hover:border-primary/40 transition-colors">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between gap-2">
                        <CardTitle className="text-base font-default font-bold text-foreground">
                          {area.name}
                        </CardTitle>
                        <Badge variant="outline" className="text-xs shrink-0 font-mono">
                          {area.zip}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <CardDescription className="text-xs text-muted-foreground font-sans leading-relaxed">
                        {area.desc}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </motion.div>

            {/* Quick Common Issues Handled */}
            <div className="p-5 rounded-xl bg-muted/60 border border-border">
              <h4 className="font-default font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
                <Wrench className="size-4 text-primary" />
                Immediate On-Site Solutions For Jonesboro Properties:
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs text-muted-foreground font-sans">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                  Burst and leaking pipe repairs
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                  Main sewer line drain clearing
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                  Emergency water heater leak fixes
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                  Slab leak identification and repair
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Urgent Dispatch Card & Service Area Map Image */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {/* Urgent Dispatch Action Box */}
            <Card className="bg-primary text-primary-foreground border-transparent shadow-md overflow-hidden relative">
              <div className="absolute top-0 right-0 translate-x-8 -translate-y-8 size-36 bg-accent/20 rounded-full blur-2xl pointer-events-none" />
              
              <CardHeader className="pb-3 relative z-10">
                <div className="inline-flex items-center gap-1.5 text-accent text-xs font-semibold uppercase tracking-wider">
                  <AlertCircle className="size-4" />
                  Immediate Dispatch Available
                </div>
                <CardTitle className="text-2xl font-default text-primary-foreground font-bold mt-1">
                  Have a Plumbing Emergency in Jonesboro?
                </CardTitle>
                <CardDescription className="text-primary-foreground/85 text-sm">
                  Do not wait for water damage to ruin your home. Call our 24-hour hotline for instant dispatch to your address.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4 relative z-10">
                <div className="p-4 rounded-lg bg-primary-foreground/10 border border-primary-foreground/20 backdrop-blur-xs">
                  <p className="text-xs text-primary-foreground/80 mb-1 font-medium">Jonesboro Emergency Dispatch Line</p>
                  <a href="tel:4708479076" className="text-xl sm:text-2xl font-bold font-mono tracking-tight text-accent hover:underline flex items-center gap-2">
                    <PhoneCall className="size-5 shrink-0" />
                    (470) 847-9076
                  </a>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button asChild variant="default" size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold w-full">
                    <a href="tel:4708479076">
                      Call Technician Now
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent w-full">
                    <Link to="/contact/">
                      Request Service
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Service Area Visual Reference Card */}
            <Card className="overflow-hidden border-border">
              <div className="relative aspect-video w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_service_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_service_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_service_map.png-wvc-sizes" alt="Jonesboro GA and Clayton County emergency plumbing service coverage area map" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-linear-to-t from-foreground/80 via-foreground/20 to-transparent flex items-end p-4">
                  <div className="text-background">
                    <p className="text-xs font-semibold uppercase tracking-wider text-accent">Clayton County, Georgia</p>
                    <p className="text-sm font-medium">Headquartered locally in Jonesboro, GA 30236</p>
                  </div>
                </div>
              </div>
              <CardContent className="p-4 bg-card text-card-foreground">
                <div className="flex items-center justify-between text-xs text-muted-foreground font-sans">
                  <span>Standard Response Radius: 20 Miles</span>
                  <Link to="/service-areas/" className="text-primary font-medium hover:underline inline-flex items-center gap-1">
                    All service areas
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}